import { SelectListItemWithSubtext } from '../AllTypesExports';
import { CustomFieldValue } from '../CustomField/CustomFieldValue';
import { DtoTicket, Ticket } from './DtoTicket';
import { ProductQuantity } from '../ecommerce/DtoProductQuantity';
import { DtoAddress, createDtoAddress } from '../membership/DtoAddress';
import { RandomGenerator } from '../../services/RandomGenerator';
import { IEventRegistrationGlobals } from '../../../pages/event-registration/EventRegistrationGlobals';

declare let noviNs: IEventRegistrationGlobals;

export interface DtoAttendee {
    customerId: number | null;
    name: string;
    email: string | null;
    phone: string | null;
    jobTitle: string | null;
    mobilePhone: string | null;
    companyName: string;
    shippingAddress: DtoAddress | null;
    credentials: string | null;
    ineligibleTickets: number[];
    alreadyPurchasedTickets: number[];
    selectedTickets: Ticket[];
    selectedAddons: ProductQuantity[];
    image: string | null;
    attendeeCustomFieldValues: { [key: string]: CustomFieldValue };
    customerCustomFieldValues: { [key: string]: CustomFieldValue };
    uniqueId: string | null;
}

export class Attendee implements DtoAttendee {
    customerId!: number | null;
    name!: string;
    email!: string | null;
    phone!: string | null;
    jobTitle!: string | null;
    mobilePhone!: string | null;
    companyName!: string;
    shippingAddress!: DtoAddress | null;
    credentials!: string | null;
    ineligibleTickets!: number[];
    alreadyPurchasedTickets!: number[];
    selectedTickets!: Ticket[];
    selectedAddons!: ProductQuantity[];
    image!: string | null;
    attendeeCustomFieldValues!: { [key: string]: CustomFieldValue; };
    customerCustomFieldValues!: { [key: string]: CustomFieldValue; };
    uniqueId!: string | null;

    constructor(dtoAttendee: DtoAttendee) {
        Object.assign(this, dtoAttendee);
    }

    get isGuest() {
        return this.customerId ? false : true;
    }

    get subTotal() {
        var subtotal = 0;
        this.selectedTickets.forEach(ticket => subtotal += ticket.price);
        this.selectedAddons.forEach(addon => subtotal += addon.subTotal);
        return subtotal;
    }

    get taxableSubTotal() { 
        var taxableSubTotal = 0;
        this.selectedTickets.forEach(ticket => {
            if (ticket.taxable) {
                taxableSubTotal += ticket.price;
            }
        });
        this.selectedAddons.forEach(addon => {
            if (addon.taxable) {
                taxableSubTotal += addon.subTotal;
            }
        });
        return taxableSubTotal;
    }

    get ticketName() {
        if (this.selectedTickets && this.selectedTickets[0]) {
            return this.selectedTickets[0].name;
        }
    }

    get customerInitial() {
        if (this.name && this.name.length > 0) {
            return this.name.charAt(0);
        }
        return '';
    }

    get isTbd() {
        return this.name == noviNs.tbdEventName;
    }

    isEligibleFor(ticketID: number) {
        return !this.ineligibleTickets || !this.ineligibleTickets.includes(ticketID);
    }

    hasAlreadyPurchased(ticketID: number) {
        return this.alreadyPurchasedTickets && this.alreadyPurchasedTickets.includes(ticketID);
    }
     
    hasSelectedTicket(ticketID: number) {
        return this.selectedTickets.some(t => t.id == ticketID);
    }

    matches(other: Attendee) {
        if (this.customerId && this.customerId === other.customerId) {
            return true;
        }

        if (this.name && this.name != noviNs.tbdEventName && this.name === other.name && this.companyName === other.companyName) {
            return true;
        }

        if (this.uniqueId && this.uniqueId === other.uniqueId) {
            return true;
        }

        return false;
    }

    setAsTbd() {
        Object.assign(this, createAttendee({
            name: noviNs.tbdEventName,
            uniqueId: this.uniqueId,
            selectedTickets: this.selectedTickets,
            selectedAddons: this.selectedAddons
        }));
    }
}

// Ticket based 
//    - one DtoAttendee for each ticket purchased
//    - one selectedTicket per attendee
//    - selectedAddons will be empty, addons are global in Ticket based so they're attached to the DtoRegistraion
//    - customFields only apply to the lone selected ticket

// Attendee based 
//    - one DtoAttendee for each customer 
//    - selectedTickets contains the tickets for this customer
//    - selectedAddons contains the add ons for this customer
//    - customFields apply to all tickets and addons for this customer, each customFieldId should only appear once 

export function createAttendee(dtoAttendee?: DtoAttendee | any) {
    let obj = <DtoAttendee>{
        customerId: null,
        name: '',
        image: '',
        email: '',
        phone: '',
        mobilePhone: '',
        jobTitle: '',
        companyName: '',
        shippingAddress: createDtoAddress(null),
        credentials: '',
        ineligibleTickets: [],
        alreadyPurchasedTickets: [],
        selectedTickets: [],
        selectedAddons: [],
        attendeeCustomFieldValues: {},
        customerCustomFieldValues: {},
        uniqueId: RandomGenerator.generateRandomString()
    }; 

    if (dtoAttendee) {

        if (dtoAttendee.image) {
            obj.image = dtoAttendee.image;
        }
        else {
            obj.image = '/img/missing-individual.jpg';
        }

        Object.assign(obj, dtoAttendee);
    }

    return new Attendee(obj);
}

export function createAttendeeFromSelectListItemWithSubText(dtoAttendee?: SelectListItemWithSubtext | any) {
    let obj = <DtoAttendee>{
        customerId: null,
        name: '',
        image: '',
        email: '',
        phone: '',
        mobilePhone: '',
        jobTitle: '',
        companyName: '',
        shippingAddress: createDtoAddress(null),
        credentials: '',
        ineligibleTickets: [],
        alreadyPurchasedTickets: [],
        selectedTickets: [],
        selectedAddons: [],
        attendeeCustomFieldValues: {},
        customerCustomFieldValues: {},
        uniqueId: RandomGenerator.generateRandomString()
    };

    obj.customerId = dtoAttendee.id;
    obj.name = dtoAttendee.text;
    obj.email = dtoAttendee.subtext;

    if (dtoAttendee.image) {
        obj.image = dtoAttendee.image;
    }
    else {
        obj.image = '/img/missing-individual.jpg';
    }

    return new Attendee(obj);
}